import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconCheck } from '@tabler/icons-react';
import { isCurrentRoute, navigateTo } from '@epihub/web/src/routes/nav';
import { bookCalendarRoute, homeChatRoute } from '@krishanmadan/core/src/routes/frontendRoutes';
import { Structure } from '@epihub/web/src/components/Structure';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.success.hex }}>{children}</h1>;
};

const bookingConfirmedStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  wizard: {
    en: [
      { text: 'The wizard consulted the ancient calendar.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'The stars have aligned:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: "Il mago consultò l'antico calendario.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'Le stelle si sono allineate:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel contains every possible reservation.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'We found yours:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele contiene ogni possibile prenotazione.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Abbiamo trovato la tua:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  philosopher: {
    en: [
      { text: 'A philosopher asked: "Is a booking real if no one confirms it?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'We answered:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un filosofo chiese: "Una prenotazione esiste se nessuno la conferma?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Abbiamo risposto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  nasa: {
    en: [
      { text: 'Mission Control ran the final checks.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Houston, we have confirmation:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Il Controllo Missione ha eseguito i controlli finali.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Houston, abbiamo la conferma:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  pigeon: {
    en: [
      { text: 'A carrier pigeon was dispatched with your request.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It returned with a scroll:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un piccione viaggiatore è partito con la tua richiesta.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'È tornato con una pergamena:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  oracle: {
    en: [
      { text: 'The Oracle of Delphi gazed into the mists of time.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'She spoke:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: "L'Oracolo di Delfi scrutò nelle nebbie del tempo.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Disse:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  medieval: {
    en: [
      { text: 'The royal scribe dipped his quill in ink.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'By decree of the kingdom:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: "Lo scriba reale intinse la penna nell'inchiostro.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'Per decreto del regno:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  ai: {
    en: [
      { text: 'The AI processed 47 billion possible outcomes.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The optimal result:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: "L'IA ha elaborato 47 miliardi di possibili esiti.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Il risultato ottimale:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  gandalf: {
    en: [
      { text: 'A wizard is never late, nor is he early.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'He confirms precisely when he means to:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un mago non è mai in ritardo, né in anticipo.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Conferma precisamente quando intende farlo:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  captain: {
    en: [
      { text: "The captain checked the ship's manifest.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'Your passage is secured:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Il capitano controllò il manifesto della nave.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Il tuo passaggio è assicurato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
  simple: {
    en: [
      { text: 'We checked. We double-checked.', Wrapper: BaseText },
      { ms: 800 },
      { text: "You're all set:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Confirmed', Wrapper: Heading },
    ],
    it: [
      { text: 'Abbiamo controllato. Ricontrollato.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Tutto pronto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Confermata', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(bookingConfirmedStories);
const randomStory = () => bookingConfirmedStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

export const BookingSuccessView = () => {
  const language = useGlobalStore((store) => store.settings.language);
  const [story] = React.useState(randomStory);

  const theme = useTheme();
  return (
    <Root>
      <Center>
        <Structure hcenter>
          <TextGenerateEffect
            words={story[language]}
          />
          <ButtonWrapper>
            <HoverBorderGradient
              containerClassName='rounded-full'
              as='button'
              className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 overridepadding'
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                if (isCurrentRoute(bookCalendarRoute, window.location)) {
                  // We're in the calendar view
                  navigateTo(homeChatRoute);
                  return;
                }

                // We're in the drawer
                useGlobalStore.getState().settings.setCalendarDrawerOpen(false);
              }}
            >
              <IconCheck color={theme.v0.col.success.hex} />
              <span>Close</span>
            </HoverBorderGradient>
          </ButtonWrapper>
        </Structure>
      </Center>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  :active {
    transform: scale(0.90);
  }
  .overridepadding {
    padding-block: 9px !important;
    padding-inline: 26px !important;
  }
`;
